import { GroupType } from '@bp/bp-graphql-types';
import { GroupRoles } from '@bp/pim-auth-constants';
import { ArrowLeftIcon, ArrowRightIcon, ModalBottomButtons } from '@bp/ui-components';
import { getFragmentData, graphql } from 'client/gql';
import { Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { uniqueByProfileUuid } from 'utils/uniquByUuid';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { splitProfilesByOrganization } from '../../../utils/splitProfilesByOrganization';
import { CollaborationAdminsValues, CourseMember } from '../types/types';
import { CourseBaseDataForm, CourseBaseDataFormDocument } from './CourseBaseDataForm';
import { CourseCollaborationForm } from './CourseCollaborationForm';
import styles from './CourseForm.module.scss';
import { CourseProfilesForm } from './CourseProfilesForm';

const CreateCoursesMutationDocument = graphql(`
  mutation BpCreateCourseForm($input: BpGroupCreateInput!) {
    createBpGroup(input: $input) {
      uuid
      __typename
    }
  }
`);

export const UpdateCoursesMutationDocument = graphql(`
  mutation BpUpdateCourseForm($update: BpGroupUpdateInput!, $uuid: ID!) {
    updateBpGroup(update: $update, uuid: $uuid) {
      uuid
      __typename
      name
      viewers {
        uuid
        __typename
      }
      admins {
        uuid
        __typename
      }
      editors {
        uuid
        __typename
      }
    }
  }
`);

const CourseFormPageQueryDocument = graphql(`
  query CourseFormPageData($courseUuid: ID!, $organizationUuid: ID!) {
    courses: groups(where: { uuid: $courseUuid }) {
      __typename
      uuid
      name
      schoolYear {
        __typename
        uuid
      }
      subject {
        __typename
        uuid
      }
      admins {
        __typename
        uuid
        displayName
        organizationRoles
        organization {
          __typename
          uuid
        }
      }
      editors {
        __typename
        uuid
        displayName
        organizationRoles
      }
      viewers {
        __typename
        uuid
        displayName
        organizationRoles
      }
      classOrClassGroup {
        __typename
        ... on Class {
          __typename
          uuid
        }
        ... on ClassGroup {
          __typename
          uuid
        }
      }
    }
    ...CourseBaseDataFormFields
    ...CourseCollaborationFormFields
  }
`);

export type CourseFormType = {
  name: string;
  schoolYear: string;
  subject: string;
  classes: string[];
  classGroups: string[];
  admins: CourseMember[];
  editors: CourseMember[];
  viewers: CourseMember[];
  collaborationSchools: string[];
  collaborationAdmins: CollaborationAdminsValues;
  collaborating: boolean;
};

export type CourseFormProps = {
  courseUuid: string | null;
  schoolYearUuid: string;
  page: 'courses' | 'institution';
  onStep: (step: number) => void;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const CourseForm = ({ courseUuid, schoolYearUuid, page, onStep, onLoad, onClose }: CourseFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const profile = pimAuthClaims.getProfile();
  const profileUuid = profile.uuid;

  const [{ data }] = useQuery({
    query: CourseFormPageQueryDocument,
    variables: {
      organizationUuid,
      courseUuid: courseUuid ?? '',
    },
  });

  const baseDataFragment = getFragmentData(CourseBaseDataFormDocument, data);

  const currentCourse = data?.courses[0];

  const isCoursesPage = page === 'courses';

  const [step, setStep] = useState<number>(0);
  const [maxSteps, setMaxSteps] = useState<number>(0);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalHint, setModalHint] = useState<string | null>(null);

  const [, createCourse] = useMutation(CreateCoursesMutationDocument);
  const [, updateCourse] = useMutation(UpdateCoursesMutationDocument);

  const handleSubmit = async (values: CourseFormType, helper: FormikHelpers<CourseFormType>) => {
    const errors = validate(values);
    if (Object.keys(errors).length > 0) return;

    onLoad(true);

    const {
      name,
      schoolYear,
      subject,
      classes: classUuids,
      classGroups: classGroupUuids,
      admins,
      editors,
      viewers,
      collaborationAdmins,
      collaborating,
    } = values;

    const classes = baseDataFragment?.classes ?? [];
    const classGroups = baseDataFragment?.classGroups ?? [];

    const uniqueEditorUuids = uniqueByProfileUuid(editors).map((e) => e.profileUuid);
    const uniqueViewerUuids = uniqueByProfileUuid(viewers).map((v) => v.profileUuid);
    const adminUuids = admins.map((a) => a.profileUuid);
    const collaborationAdminUuids = Object.values(collaborationAdmins)
      .flatMap((v) => v?.map((item) => item.uuid))
      .filter((item) => item);
    const uniqueAdminUuids = Array.from(new Set([...adminUuids, ...collaborationAdminUuids]));

    // find classes without a selected group (hasMembersFrom needs classUuid if no classGroup is selected otherwise classGroupUuid)
    const classesWithoutSelectedGroups = classes.filter((c) => {
      if (!classUuids.includes(c.uuid)) return false;
      // check if any selected classGroup belongs to this class
      const hasSelectedGroup = classGroups.some(
        (g) =>
          classGroupUuids.includes(g.uuid) && c.divisionsConnection.edges.some((e) => e.node.uuid === g.division.uuid),
      );
      return !hasSelectedGroup;
    });

    const input = {
      name,
      schoolYear,
      subject,
      // uuid GANZER Klassen oder falls für eine Klasse eine Gruppe gewählt wurde die classGroups
      hasMembersFrom: [...classesWithoutSelectedGroups.map((c) => c.uuid), ...classGroupUuids],
      admins: uniqueAdminUuids,
      editors: uniqueEditorUuids,
      viewers: uniqueViewerUuids,
    };

    if (courseUuid) {
      const { error } = await updateCourse({
        update: input,
        uuid: courseUuid,
      });
      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessUpdateToast();
      }
    } else {
      const { error } = await createCourse({
        input: {
          organization: pimAuthClaims.getOrganizationUuid(),
          groupType: GroupType.Course,
          foreignRefId: [],
          isCommunity: collaborating,
          ...input,
        },
      });
      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessCreateToast();
      }
    }

    onLoad(false);
    helper.resetForm();
    onClose();
  };

  const otherOrganizations = currentCourse?.admins
    ? splitProfilesByOrganization(
        currentCourse.admins.map((a) => ({
          uuid: a.uuid,
          organization: a.organization,
          displayName: a.displayName ?? '',
        })),
        organizationUuid,
      )
    : null;

  const admins: CourseMember[] =
    currentCourse?.admins
      .filter((admin) => admin.organization.uuid === organizationUuid)
      .map((admin) => {
        return {
          profileUuid: admin.uuid,
          name: admin.displayName ?? '',
          groupRole: GroupRoles.Admin,
          organizationRoles: admin.organizationRoles,
        };
      }) ??
    (isCoursesPage
      ? [
          {
            profileUuid: profileUuid,
            name: profile.displayName ?? '',
            groupRole: GroupRoles.Admin,
            organizationRoles: ['TEACHER'], // hacky
          },
        ]
      : []);

  const editors: CourseMember[] =
    currentCourse?.editors.map((editor) => {
      return {
        profileUuid: editor.uuid,
        name: editor.displayName ?? '',
        groupRole: GroupRoles.Editor,
        organizationRoles: editor.organizationRoles,
      };
    }) ??
    (isCoursesPage
      ? [
          {
            profileUuid: profileUuid,
            name: profile.displayName ?? '',
            groupRole: GroupRoles.Editor,
            organizationRoles: ['TEACHER'], // hacky
          },
        ]
      : []);

  const viewers: CourseMember[] =
    currentCourse?.viewers.map((viewer) => {
      return {
        profileUuid: viewer.uuid,
        name: viewer.displayName ?? '',
        groupRole: GroupRoles.Viewer,
        organizationRoles: viewer.organizationRoles,
      };
    }) ?? [];

  const completeClasses: string[] =
    currentCourse?.classOrClassGroup.filter((c) => c.__typename === 'Class').map((c) => c.uuid) ?? [];

  const classesWithSelectedGroups = (
    baseDataFragment?.classes.filter((c) => c.schoolYear.uuid === schoolYearUuid) ?? []
  ).filter((c) => {
    // get all classGroup uuids from currentCourse that are ClassGroup
    const selectedClassGroupUuids =
      currentCourse?.classOrClassGroup.filter((cg) => cg.__typename === 'ClassGroup').map((cg) => cg.uuid) ?? [];
    // check if any classGroup for this class is selected in currentCourse
    return (baseDataFragment?.classGroups ?? []).some(
      (g) =>
        selectedClassGroupUuids.includes(g.uuid) &&
        c.divisionsConnection.edges.some((e) => e.node.uuid === g.division.uuid),
    );
  });

  const initialValues: CourseFormType = {
    name: currentCourse?.name ?? '',
    schoolYear: currentCourse?.schoolYear?.uuid ?? schoolYearUuid,
    subject: currentCourse?.subject?.uuid ?? '',
    classes: [...completeClasses, ...classesWithSelectedGroups.map((c) => c.uuid)],
    classGroups: currentCourse?.classOrClassGroup.filter((c) => c.__typename === 'ClassGroup').map((c) => c.uuid) ?? [],
    editors,
    viewers,
    admins,
    collaborationAdmins: otherOrganizations ?? {},
    collaborationSchools: otherOrganizations ? Object.keys(otherOrganizations) : [],
    collaborating: !!otherOrganizations,
  };

  function handleCollaborationChange(isCollaboration: boolean) {
    setMaxSteps(isCollaboration && !isCoursesPage ? 2 : 1);
  }

  function validate(values: CourseFormType) {
    const errors: FormikErrors<CourseFormType> = {};
    setModalError(null);

    if (step === 0) {
      if (!values.name || values.name === '') {
        errors.name = t('validation.required.designation');
      }
      if (!values.editors || values.editors.length === 0) {
        errors.editors = t('validation.required.teachers');
      }
      if (!values.schoolYear) {
        errors.schoolYear = t('validation.required.schoolyear');
      }
      if (!values.subject) {
        errors.subject = t('validation.required.subject');
      }
      if (!values.admins || values.admins.length === 0) {
        errors.admins = t('validation.required.admins');
      }
      if (!values.classes || values.classes.length === 0) {
        errors.classes = t('validation.required.classes');
      }
    }

    if (step === 2) {
      if (values.collaborating && values.collaborationSchools.length === 0) {
        setModalError(t('validation.required.collaborationSchools'));
        errors.collaborationSchools = t('validation.required.collaborationSchools');
      }
      if (values.collaborationSchools.length > 0) {
        values.collaborationSchools.forEach((schoolUuid) => {
          const admins = values.collaborationAdmins[schoolUuid];
          if (!admins || admins.length === 0) {
            if (!errors.collaborationAdmins) {
              errors.collaborationAdmins = {};
            }
            errors.collaborationAdmins[schoolUuid] = t('validation.required.collaborationAdmins');
          }
        });
      }
    }

    return errors;
  }

  useEffect(() => {
    setMaxSteps(!!otherOrganizations && !isCoursesPage ? 2 : 1);
  }, [isCoursesPage, otherOrganizations]);

  useEffect(() => {
    onStep(step);
  }, [onStep, step]);

  return (
    <Formik<CourseFormType> onSubmit={handleSubmit} initialValues={initialValues} validate={validate}>
      {({ errors, validateForm }) => (
        <Form className={styles['course-form']}>
          <div className={styles.slider}>
            <div
              tabIndex={0}
              className={styles.slide}
              style={{
                left: step === 0 ? '0' : step > 0 ? `${step * -100}%` : `${step * 100}%`,
              }}
            >
              <CourseBaseDataForm page={page} onCollaborationChange={handleCollaborationChange} query={data} />
            </div>

            <div
              tabIndex={0}
              className={styles.slide}
              style={{ left: step === 1 ? '0' : step > 1 ? `${(step + 1) * -100}%` : `${(step + 1) * 100}%` }}
            >
              <CourseProfilesForm step={step} onHint={setModalHint} />
            </div>

            <div
              tabIndex={0}
              className={styles.slide}
              style={{ left: step === 2 ? '0' : step > 2 ? `${(step + 2) * -100}%` : `${(step + 2) * 100}%` }}
            >
              <CourseCollaborationForm onLoad={onLoad} query={data} />
            </div>
          </div>

          <ModalBottomButtons
            className={'mt-5'}
            closeButton={{
              callback: onClose,
            }}
            additionalLeftComponent={
              modalError ? (
                <div style={{ color: 'var(--color-error)' }}>{`${t('common.error')}: ${modalError}`}</div>
              ) : modalHint ? (
                modalHint
              ) : undefined
            }
            submitButton={{
              disabled: Object.keys(errors).length > 0 || step !== maxSteps,
            }}
            additionalButtons={[
              {
                text: t('common.back'),
                icon: <ArrowLeftIcon className='small' />,
                disabled: step === 0,
                callback: () => {
                  setStep((prev) => prev - 1);
                  validateForm();
                },
              },
              {
                text: t('common.next'),
                icon: <ArrowRightIcon className='small' />,
                iconPosition: 'right',
                disabled: Object.keys(errors).length > 0 || step === maxSteps,
                callback: () => {
                  setStep((prev) => prev + 1);
                  validateForm();
                },
              },
            ]}
          />
        </Form>
      )}
    </Formik>
  );
};
